import React, { useState, useEffect } from "react";
import { fetchWrapper } from "utils/apiFetch";

function DTHRecharge() {
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [pin, setMPIN] = useState("");
  const [operator, setOperator] = useState("");
  const [operators, setOperators] = useState([]);
  const [circles, setCircles] = useState([]);
  const [error, setError] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [dthDetails, setDthDetails] = useState(null);

  // Plan-related states
  const [planData, setPlanData] = useState(null);
  const [activeTab, setActiveTab] = useState("TOPUP");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // DTH Channel Plan states
  const [dthChannelData, setDthChannelData] = useState(null);
  const [dthChannelLoading, setDthChannelLoading] = useState(false);
  const [dthChannelSearchTerm, setDthChannelSearchTerm] = useState("");
  const [activeDthTab, setActiveDthTab] = useState("Plan");

  // DTH R Offer states
  const [dthROfferData, setDthROfferData] = useState(null);
  const [dthROfferLoading, setDthROfferLoading] = useState(false);
  const [dthROfferSearchTerm, setDthROfferSearchTerm] = useState("");

  // Helper function to create valid CSS selector IDs
  const createValidId = (text) => {
    return text.replace(/[^a-zA-Z0-9]/g, '_').replace(/^[0-9]/, 'tab_$&');
  };

  // Fetch Operators & Circles on page load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const opRes = await fetchWrapper("https://api.mokshiq.in/fetch_dth_operator?type=dth");
        const opJson = opRes;
        setOperators(opJson.data || []);
      } catch (err) {
        console.error("Error loading operators/circles", err);
      }
    };

    fetchData();
  }, []);

  // Auto-detect Operator & Circle when mobile reaches required digits
  useEffect(() => {
    if (!operator || !mobile) return;
  
    let requiredLength = 10; // default
    if (operator.toLowerCase() === "dish tv" || operator.toLowerCase() === "sun direct") {
      requiredLength = 11;
    } else if (operator.toLowerCase() === "videocon") {
      requiredLength = 9;
    }
  
    if (mobile.length === requiredLength) {
      const fetchDthInfo = async () => {
        try {
          setRechargeLoading(true);
          const res = await fetchWrapper(
            `https://api.mokshiq.in/fetch_dth_info?phone=${mobile}&operator=${operator}`
          );
          
          const json = res;
  
          if (json?.status && json?.data?.records?.length > 0) {
            setDthDetails(json.data.records[0]);
          } else {
            setDthDetails(null);
            showToast("No details found for this DTH number", "warning");
          }
        } catch (err) {
          console.error("DTH Info Lookup failed:", err);
          showToast("Failed to fetch DTH details", "danger");
        } finally {
          setRechargeLoading(false);
        }
      };
  
      fetchDthInfo();
    }
  }, [mobile, operator]);
  
  // Fetch plan data
  const fetchPlanData = async () => {
    if (!operator) {
      alert("Please select operator first");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetchWrapper(
        `https://api.mokshiq.in/fetch_dth_plan?operator=${encodeURIComponent(operator)}`
      );
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        setPlanData(data.data.records);
        // Set first category as active tab
        const firstCategory = Object.keys(data.data.records)[0];
        if (firstCategory) {
          setActiveTab(firstCategory);
        }
      } else {
        setPlanData(null);
        showToast("No plans found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setPlanData(null);
      showToast("Failed to fetch plans", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Fetch DTH Channel Plans
  const fetchDthChannelPlans = async () => {
    if (!operator) {
      alert("Please select operator first");
      return;
    }
  
    setDthChannelLoading(true);
    try {
      const response = await fetchWrapper(`https://api.mokshiq.in/fetch_dth_plan_channel?operator=${encodeURIComponent(operator)}`);
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        setDthChannelData(data.data.records);
        // Set first category as active tab
        const firstCategory = Object.keys(data.data.records)[0];
        if (firstCategory) {
          setActiveDthTab(firstCategory);
        }
      } else {
        setDthChannelData(null);
        showToast("No channel plans found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching DTH channel plans:", err);
      setDthChannelData(null);
      showToast("Failed to fetch DTH channel plans", "danger");
    } finally {
      setDthChannelLoading(false);
    }
  };

  // Fetch DTH R Offer data
  const fetchDthROfferData = async () => {
    if (!operator) {
      alert("Please select operator first");
      return;
    }
  
    setDthROfferLoading(true);
    try {
      const response = await fetchWrapper(`https://api.mokshiq.in/fetch_dth_roffer?operator=${encodeURIComponent(operator)}`);
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        if (data.data.records.status === 0) {
          // Plan not available
          setDthROfferData(null);
          showToast(data.data.records.desc || "DTH R Offer not available for this operator", "warning");
        } else {
          setDthROfferData(data.data.records);
        }
      } else {
        setDthROfferData(null);
        showToast("No DTH R Offer found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching DTH R Offer:", err);
      setDthROfferData(null);
      showToast("Failed to fetch DTH R Offer", "danger");
    } finally {
      setDthROfferLoading(false);
    }
  };

  // DTH Heavy Refresh function
  const performDthHeavyRefresh = async () => {
    if (!operator || !mobile) {
      alert("Please select operator and enter DTH number first");
      return;
    }
  
    setRechargeLoading(true);
    try {
      const response = await fetchWrapper(`https://api.mokshiq.in/dth_heavy_refresh?operator=${encodeURIComponent(operator)}&phone=${mobile}`);
      const data = response;
      
      if (data.status) {
        showToast("DTH Heavy refresh completed successfully", "success");
        // Optionally refresh DTH details after heavy refresh
        if (mobile.length >= 9) {
          const fetchDthInfo = async () => {
            try {
              const res = await fetchWrapper(
                `https://api.mokshiq.in/fetch_dth_info?phone=${mobile}&operator=${operator}`
              );
              const json = res;;
              if (json?.status && json?.data?.records?.length > 0) {
                setDthDetails(json.data.records[0]);
              }
            } catch (err) {
              console.error("DTH Info Lookup failed:", err);
            }
          };
          fetchDthInfo();
        }
      } else {
        showToast(data.message || "DTH Heavy refresh failed", "danger");
      }
    } catch (err) {
      console.error("Error in DTH Heavy refresh:", err);
      showToast("Failed to perform DTH Heavy refresh", "danger");
    } finally {
      setRechargeLoading(false);
    }
  };

  // Handle mobile number input (only digits)
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setMobile(value);
  };

  // Handle plan selection
  const handlePlanSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom1');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Handle DTH channel plan selection
  const handleDthChannelSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom2');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Handle DTH R Offer selection
  const handleDthROfferSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom3');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Filter plans
  const filterPlans = (plans) => {
    if (!searchTerm) return plans;
    return plans.filter(plan => 
      (plan.rs && Object.values(plan.rs).some(price => price.toString().includes(searchTerm))) ||
      (plan.desc && plan.desc.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (plan.plan_name && plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Filter DTH channel plans
  const filterDthChannelPlans = (plans) => {
    if (!dthChannelSearchTerm) return plans;
    return plans.filter(plan => 
      (plan.rs && Object.values(plan.rs).some(price => price.toString().includes(dthChannelSearchTerm))) ||
      (plan.desc && plan.desc.toLowerCase().includes(dthChannelSearchTerm.toLowerCase())) ||
      (plan.plan_name && plan.plan_name.toLowerCase().includes(dthChannelSearchTerm.toLowerCase()))
    );
  };

  // Filter DTH R Offer data
  const filterDthROfferData = (data) => {
    if (!dthROfferSearchTerm || !Array.isArray(data)) return data;
    return data.filter(item => 
      (item.rs && item.rs.toString().includes(dthROfferSearchTerm)) ||
      (item.desc && item.desc.toLowerCase().includes(dthROfferSearchTerm.toLowerCase())) ||
      (item.plan_name && item.plan_name.toLowerCase().includes(dthROfferSearchTerm.toLowerCase()))
    );
  };

  const showToast = (message, type = "primary") => {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible cookies-alert position-fixed top-0 end-0 m-3 shadow`;
    alertDiv.setAttribute("role", "alert");
    alertDiv.style.zIndex = 2000;

    alertDiv.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0 flex-grow-1">${message}</p>
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv?.remove(), 3000);
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (mobile.length < 9) {
      setError("DTH number must be at least 9 digits.");
      return;
    }
    if (!amount) {
      setError("Please enter recharge amount.");
      return;
    }
    if (!operator) {
      setError("Please select operator.");
      return;
    }
    
    setRechargeLoading(true);
    try {
        const formData = new FormData();
        formData.append("number", mobile);
        formData.append("operator", operator);
        formData.append("amount", amount);
        formData.append("user_id", 3);
        formData.append("pin", pin);
    
        const response = await fetchWrapper("https://api.mokshiq.in/create_dth_recharge", {
          method: "POST",
           headers: {
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
        },
          body: formData,
        });
    
        const data = await response.json();
    
        if (data.status) {
            showToast(data.message || "Success!", "success");
            setMobile("");
            setAmount("");
            setOperator("");
            setError("");
        } else {
            showToast(data.message || "Something went wrong!", "danger");
        }
      } catch (err) {
        console.error("Recharge API Error:", err);
        showToast("Something went wrong!", "danger");
      } finally {
        setRechargeLoading(false);
      }
  };

  return (
    <main>
      <div className="container-fluid">
        <div className="row m-1">
          <div className="col-12 ">
            <h4 className="main-title">DTH Recharge</h4>
            <ul className="app-line-breadcrumbs mb-3">
              <li>
                <a className="f-s-14 f-w-500" href="#">
                  <span>
                    <i className="ph-duotone ph-briefcase-metal f-s-16"></i>{" "}
                    Services
                  </span>
                </a>
              </li>
              <li className="active">
                <a className="f-s-14 f-w-500" href="#">
                  DTH Recharge
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Form Section */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              {rechargeLoading && (
                <div 
                  className="page-loader-overlay d-flex justify-content-center align-items-center"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    zIndex: 3000,
                  }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              
              <div className="card-body">
                <div className="app-form">
                  <div className="row">
                    {/* Operator Dropdown */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Select Operator</label>
                        <select
                          className="form-select"
                          value={operator}
                          onChange={(e) => setOperator(e.target.value)}
                          required
                        >
                          <option value="">-- Select Operator --</option>
                          {operators.map((op, i) => (
                            <option key={i} value={op.name}>
                              {op.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Mobile Number */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">DTH Number</label>
                        <input
                          className="form-control"
                          placeholder="Enter Your DTH Number"
                          value={mobile}
                          onChange={handleMobileChange}
                          required
                          type="text"
                          maxLength="11"
                        />
                      </div>
                    </div>

                    {/* Recharge Amount */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Recharge Amount</label>
                        <input
                          className="form-control"
                          placeholder="Enter Your Recharge Amount"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                          type="text"
                        />
                      </div>
                      <div className="text-end">
                        <button 
                          aria-controls="offcanvasBottom3"
                          className="btn btn-light-secondary m-2"
                          data-bs-target="#offcanvasBottom3"
                          data-bs-toggle="offcanvas"
                          type="button"
                          onClick={fetchDthROfferData}
                        >
                          DTH R Offer
                        </button>
                        <button 
                          aria-controls="offcanvasBottom2"
                          className="btn btn-light-primary m-2"
                          data-bs-target="#offcanvasBottom2"
                          data-bs-toggle="offcanvas"
                          type="button"
                          onClick={fetchDthChannelPlans}
                        >
                          DTH Channel Plan
                        </button>
                        <button
                          aria-controls="offcanvasBottom1" 
                          className="btn btn-light-primary m-2"
                          data-bs-target="#offcanvasBottom1"
                          data-bs-toggle="offcanvas" 
                          type="button"
                          onClick={fetchPlanData}
                        >
                          Fetch Plan
                        </button>
                        <button
                          className="btn btn-light-warning m-2"
                          type="button"
                          onClick={performDthHeavyRefresh}
                        >
                          Heavy Refresh
                        </button>
                      </div>
                    </div>

                    {/* Transaction Pin */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Transaction Pin</label>
                        <input
                          className="form-control"
                          placeholder="Enter Your Transaction Pin"
                          value={pin}
                          onChange={(e) => setMPIN(e.target.value)}
                          required
                          type="password"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="col-md-12">
                      <p className="text-danger">{error}</p>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="col-md-12 text-end">
                    <button className="btn btn-primary" type="button" onClick={handleSubmit}>
                      Submit
                    </button>
                    <button
                      className="btn btn-secondary ms-2"
                      type="button"
                      onClick={() => {
                        setMobile("");
                        setAmount("");
                        setOperator("");
                        setError("");
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
              
              {dthDetails && (
                <div className="card mt-3">
                  <div className="card-header">
                    <h6>Customer Details</h6>
                  </div>
                  <div className="card-body">
                    <p><strong>Name:</strong> {dthDetails.customerName}</p>
                    <p><strong>Balance:</strong> ₹{dthDetails.Balance}</p>
                    <p><strong>Status:</strong> {dthDetails.status}</p>
                    <p><strong>Plan:</strong> {dthDetails.planname}</p>
                    <p><strong>Monthly Recharge:</strong> ₹{dthDetails.MonthlyRecharge}</p>
                    <p><strong>Next Recharge Date:</strong> {dthDetails.NextRechargeDate}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Offcanvas for Plan Selection */}
        <div 
          className="offcanvas offcanvas-bottom"
          id="offcanvasBottom1"
          tabIndex="-1"
          style={{ height: '80vh' }}
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Select DTH Plan</h5>
            <button 
              className="btn-close text-reset fs-5"
              data-bs-dismiss="offcanvas"
              type="button"
            ></button>
          </div>
          <div className="offcanvas-body">
            {loading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : planData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search plans..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-3">
                  {Object.keys(planData).map((category) => {
                    const validId = createValidId(category);
                    return (
                      <li className="nav-item" key={category}>
                        <button
                          className={`nav-link ${activeTab === category ? 'active' : ''}`}
                          data-bs-toggle="tab"
                          data-bs-target={`#${validId}`}
                          type="button"
                          onClick={() => setActiveTab(category)}
                        >
                          {category.replace(/[^a-zA-Z]/g, ' ')}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {Object.entries(planData).map(([category, plans]) => {
                    const validId = createValidId(category);
                    return (
                      <div
                        key={category}
                        className={`tab-pane fade ${activeTab === category ? 'show active' : ''}`}
                        id={validId}
                      >
                        <div className="row g-3">
                          {filterPlans(plans).map((plan, index) => (
                            <div key={index} className="col-12">
                              <div className="card plan-card">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <h6 className="card-title text-primary mb-2">
                                        {plan.plan_name}
                                      </h6>
                                      <p className="card-text text-muted small mb-2">
                                        {plan.desc}
                                      </p>
                                      <div className="d-flex flex-wrap gap-2 mb-2">
                                        {plan.rs && Object.entries(plan.rs).map(([duration, price]) => (
                                          <div key={duration} className="d-flex align-items-center">
                                            <span className="badge bg-primary me-2">
                                              {duration}: ₹{price}
                                            </span>
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => handlePlanSelect(price)}
                                            >
                                              Select
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                      <small className="text-muted">
                                        Updated: {plan.last_update}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {filterPlans(plans).length === 0 && (
                          <div className="text-center py-4">
                            <p className="text-muted">No plans found matching your search.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Click "Fetch Plan" to load available plans.</p>
              </div>
            )}
          </div>
        </div>

        {/* Offcanvas for DTH Channel Plans */}
        <div 
          className="offcanvas offcanvas-bottom"
          id="offcanvasBottom2"
          tabIndex="-1"
          style={{ height: '80vh' }}
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">DTH Channel Plans</h5>
            <button 
              className="btn-close text-reset fs-5"
              data-bs-dismiss="offcanvas"
              type="button"
            ></button>
          </div>
          <div className="offcanvas-body">
            {dthChannelLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : dthChannelData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search channel plans..."
                    value={dthChannelSearchTerm}
                    onChange={(e) => setDthChannelSearchTerm(e.target.value)}
                  />
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-3">
                  {Object.keys(dthChannelData).map((category) => {
                    const validId = createValidId(category);
                    return (
                      <li className="nav-item" key={category}>
                        <button
                          className={`nav-link ${activeDthTab === category ? 'active' : ''}`}
                          data-bs-toggle="tab"
                          data-bs-target={`#dth_${validId}`}
                          type="button"
                          onClick={() => setActiveDthTab(category)}
                        >
                          {category.replace(/[^a-zA-Z]/g, ' ')}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                  {Object.entries(dthChannelData).map(([category, plans]) => {
                    const validId = createValidId(category);
                    return (
                      <div
                        key={category}
                        className={`tab-pane fade ${activeDthTab === category ? 'show active' : ''}`}
                        id={`dth_${validId}`}
                      >
                        <div className="row g-3">
                          {filterDthChannelPlans(plans).map((plan, index) => (
                            <div key={index} className="col-12">
                              <div className="card plan-card">
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                      <h6 className="card-title text-primary mb-2">
                                        {plan.plan_name}
                                      </h6>
                                      <p className="card-text text-muted small mb-2">
                                        {plan.desc}
                                      </p>
                                      <div className="d-flex flex-wrap gap-2 mb-2">
                                        {plan.rs && Object.entries(plan.rs).map(([duration, price]) => (
                                          <div key={duration} className="d-flex align-items-center">
                                            <span className="badge bg-primary me-2">
                                              {duration}: ₹{price}
                                            </span>
                                            <button
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => handleDthChannelSelect(price)}
                                            >
                                              Select
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                      <small className="text-muted">
                                        Updated: {plan.last_update}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {filterDthChannelPlans(plans).length === 0 && (
                          <div className="text-center py-4">
                            <p className="text-muted">No channel plans found matching your search.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Click "DTH Channel Plan" to load available channel plans.</p>
              </div>
            )}
          </div>
        </div>

        {/* Offcanvas for DTH R Offer */}
        <div 
          className="offcanvas offcanvas-bottom"
          id="offcanvasBottom3"
          tabIndex="-1"
          style={{ height: '80vh' }}
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">DTH R Offer</h5>
            <button 
              className="btn-close text-reset fs-5"
              data-bs-dismiss="offcanvas"
              type="button"
            ></button>
          </div>
          <div className="offcanvas-body">
            {dthROfferLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : dthROfferData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search R Offer..."
                    value={dthROfferSearchTerm}
                    onChange={(e) => setDthROfferSearchTerm(e.target.value)}
                  />
                </div>

                <div className="row g-3">
                  {Array.isArray(dthROfferData) ? (
                    filterDthROfferData(dthROfferData).map((offer, index) => (
                      <div key={index} className="col-12">
                        <div className="card plan-card">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <h6 className="card-title text-primary mb-2">
                                  {offer.plan_name || 'DTH Offer'}
                                </h6>
                                <p className="card-text text-muted small mb-2">
                                  {offer.desc}
                                </p>
                                {offer.rs && (
                                  <div className="d-flex align-items-center mb-2">
                                    <span className="badge bg-primary me-2">
                                      ₹{offer.rs}
                                    </span>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleDthROfferSelect(offer.rs)}
                                    >
                                      Select
                                    </button>
                                  </div>
                                )}
                                {offer.last_update && (
                                  <small className="text-muted">
                                    Updated: {offer.last_update}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center">
                          <p className="text-muted">{dthROfferData.desc || 'No R Offer data available'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {Array.isArray(dthROfferData) && filterDthROfferData(dthROfferData).length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">No R Offers found matching your search.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Click "DTH R Offer" to load available offers.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .plan-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
        
        .offcanvas-body {
          max-height: calc(80vh - 60px);
          overflow-y: auto;
        }

        .badge {
          font-size: 0.75em;
        }
      `}</style>
    </main>
  );
}

export default DTHRecharge;